/**
 * Measurement plots surfaced by the SHOW_MEASUREMENT_PLOT effect (AP-450).
 *
 * The reducer keeps the LATEST plot per measurement key so the same key
 * shown twice in a conversation overwrites the previous series instead
 * of stacking. Types are defined locally — the FE boundary lint forbids
 * `state/` slices from depending on `features/*` modules.
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@/state/store';

export interface TrendPoint {
  study_uuid: string;
  study_date: string | null;
  accession: string;
  value: number;
  unit: string;
  in_range: boolean | null;
  scan_product: string;
}

export interface MeasurementPlotPayload {
  measurement_key: string;
  name: string;
  unit: string;
  abbreviation: string;
  reference_range: { low: number | null; high: number | null } | null;
  series: TrendPoint[];
  summary: {
    trend: string;
    delta_pct: number | null;
    out_of_range_count: number;
    count: number;
  };
  count: number;
}

export interface MeasurementPlot {
  measurementKey: string;
  name: string;
  unit: string;
  abbreviation: string;
  referenceRange: { low: number | null; high: number | null } | null;
  series: TrendPoint[];
  summary: {
    trend: string;
    deltaPct: number | null;
    outOfRangeCount: number;
    count: number;
  };
  receivedAt: number;
}

interface CopilotPlotsState {
  byKey: Record<string, MeasurementPlot>;
  latestKey: string | null;
}

const initialState: CopilotPlotsState = { byKey: {}, latestKey: null };

const slice = createSlice({
  name: 'copilotPlots',
  initialState,
  reducers: {
    plotReceived: (state, action: PayloadAction<MeasurementPlotPayload>) => {
      const p = action.payload;
      const plot: MeasurementPlot = {
        measurementKey: p.measurement_key,
        name: p.name,
        unit: p.unit,
        abbreviation: p.abbreviation,
        referenceRange: p.reference_range,
        series: p.series,
        summary: {
          trend: p.summary.trend,
          deltaPct: p.summary.delta_pct,
          outOfRangeCount: p.summary.out_of_range_count,
          count: p.summary.count,
        },
        receivedAt: Date.now(),
      };
      state.byKey[p.measurement_key] = plot;
      state.latestKey = p.measurement_key;
    },
    plotCleared: (state, action: PayloadAction<{ measurementKey: string }>) => {
      delete state.byKey[action.payload.measurementKey];
      if (state.latestKey === action.payload.measurementKey) {
        state.latestKey = null;
      }
    },
  },
});

export const { plotReceived, plotCleared } = slice.actions;

export const selectLatestPlot = (state: RootState): MeasurementPlot | null => {
  const key = state.copilotPlots.latestKey;
  return key ? (state.copilotPlots.byKey[key] ?? null) : null;
};

export const selectPlotByKey =
  (key: string) =>
  (state: RootState): MeasurementPlot | null =>
    state.copilotPlots.byKey[key] ?? null;

export default slice.reducer;
